import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Package, ChevronLeft, RefreshCw, CheckCircle, XCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminAwaitingApprovalPage() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/shipments/awaiting-approval');
      console.log("API Response:", res.data);
      setShipments(res.data?.data || res.data || []);
    } catch (e: any) {
      console.log("API Error:", e.response?.data, e.response?.status);
      console.error(e);
      toast.error('فشل تحميل الشحنات');
    } finally {
      setLoading(false);
    }
  };

  const approve = async (id: number) => {
    try {
      setProcessing(id);
      await api.post(`/api/shipments/${id}/approve`, {});
      toast.success('تمت الموافقة على الشحنة');
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'فشلت الموافقة');
    } finally {
      setProcessing(null);
    }
  };

  const reject = async (id: number) => {
    const reason = prompt('سبب الرفض:');
    if (!reason) return;
    try {
      setProcessing(id);
      await api.post(`/api/shipments/${id}/reject`, { reason });
      toast.success('تم رفض الشحنة');
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'فشل الرفض');
    } finally {
      setProcessing(null);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = shipments.filter(s =>
    !search || (s.trackingNumber?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (s.senderName || '').includes(search) || (s.receiverName || '').includes(search)
  );

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">الشحنات الواردة</h1>
          <p className="text-sm text-gray-500 mt-1">الشحنات اللي سجلها التجار وبانتظار المراجعة والموافقة</p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-white">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="بحث..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="outline-none text-sm w-40"
            />
          </div>
          <Button variant="outline" size="sm" onClick={load} className="gap-1">
            <RefreshCw className="w-4 h-4" /> تحديث
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-right text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="p-4 font-medium text-gray-600">رقم التتبع</th>
                  <th className="p-4 font-medium text-gray-600">الراسل</th>
                  <th className="p-4 font-medium text-gray-600">المستلم</th>
                  <th className="p-4 font-medium text-gray-600">المدينة</th>
                  <th className="p-4 font-medium text-gray-600">الحالة</th>
                  <th className="p-4 font-medium text-gray-600">التاريخ</th>
                  <th className="p-4 font-medium text-gray-600">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-8 text-center text-gray-400">جاري التحميل...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-gray-400">
                    <Package className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                    لا توجد شحنات بانتظار الموافقة
                  </td></tr>
                ) : (
                  filtered.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-gray-50">
                      <td className="p-4 font-mono text-xs font-bold text-gray-900">{s.trackingNumber}</td>
                      <td className="p-4 text-gray-700">{s.senderName || '—'}</td>
                      <td className="p-4 text-gray-700">{s.receiverName || '—'}</td>
                      <td className="p-4 text-gray-500">{s.receiverCity || '—'}</td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                          <Clock className="w-3 h-3" /> بانتظار موافقة الإدارة
                        </span>
                      </td>
                      <td className="p-4 text-xs text-gray-500">
                        {s.createdAt ? new Date(s.createdAt).toLocaleDateString('ar-EG') : '—'}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white gap-1"
                            disabled={processing === s.id}
                            onClick={() => approve(s.id)}
                          >
                            <CheckCircle className="w-3 h-3" /> قبول
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50 gap-1"
                            disabled={processing === s.id}
                            onClick={() => reject(s.id)}
                          >
                            <XCircle className="w-3 h-3" /> رفض
                          </Button>
                          <Button variant="ghost" size="sm" className="text-gray-500 gap-1" onClick={() => navigate(`/shipments/${s.id}`)}>
                            <ChevronLeft className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
